/**
 * shared/types/roles.types.ts
 *
 * Tipos base del sistema de roles y permisos CELMAXX.
 * ──────────────────────────────────────────────────────────────────
 * ESTADO: Infraestructura BASE — solo tipos y definiciones.
 * NO conectado a lógica crítica. NO modifica ventas ni turnos.
 * ──────────────────────────────────────────────────────────────────
 *
 * Jerarquía de roles (mayor nivel = más acceso):
 *
 *   Nivel 6 — director      → visibilidad corporativa multi-empresa
 *   Nivel 5 — admin         → control total de una empresa
 *   Nivel 4 — gerente       → multi-tienda dentro de empresa
 *   Nivel 4 — supervisor    → operación avanzada de tienda
 *   Nivel 3 — encargado     → operación completa de tienda
 *   Nivel 2 — cajero        → ventas y operación básica
 *   Nivel 2 — vendedor      → ventas puro (sin turno/caja)
 *   Nivel 2 — tecnico       → taller y reparaciones
 *   Nivel 2 — almacenista   → inventario y almacén
 *   Nivel 2 — bodega        → legacy (alias de almacenista)
 *   Nivel 1 — contador      → lectura financiera
 *   Nivel 1 — auditor       → lectura total, sin modificaciones
 *
 * Roles legacy (conservados para compatibilidad con datos en DB):
 *   bodega  → usar almacenista para nuevos usuarios
 *   auditor → conservado, read-only total
 */

// ── Roles ──────────────────────────────────────────────────────────────────

export type Role =
  | 'director' // nivel corporativo — acceso multi-empresa
  | 'admin' // control total de empresa y tiendas
  | 'gerente' // supervisión multi-tienda dentro de empresa
  | 'supervisor' // operación avanzada de tienda (gerente de tienda)
  | 'encargado' // operación completa de su tienda
  | 'cajero' // ventas y operación básica con turno
  | 'vendedor' // ventas puro (sin abrir/cerrar turno)
  | 'tecnico' // taller: reparaciones y garantías técnicas
  | 'almacenista' // inventario: almacén, transferencias, compras
  | 'bodega' // legacy — equivale a almacenista, conservado por compatibilidad
  | 'contador' // contabilidad: lectura financiera y costos
  | 'auditor' // auditoría: lectura total, sin modificaciones

export const ROLES: Role[] = [
  'director',
  'admin',
  'gerente',
  'supervisor',
  'encargado',
  'cajero',
  'vendedor',
  'tecnico',
  'almacenista',
  'bodega',
  'contador',
  'auditor',
]

export const ROLE_LABELS: Record<Role, string> = {
  director: 'Director',
  admin: 'Administrador',
  gerente: 'Gerente',
  supervisor: 'Supervisor',
  encargado: 'Encargado',
  cajero: 'Cajero',
  vendedor: 'Vendedor',
  tecnico: 'Técnico',
  almacenista: 'Almacenista',
  bodega: 'Bodega',
  contador: 'Contador',
  auditor: 'Auditor',
}

export const ROLE_ICONS: Record<Role, string> = {
  director: '🏛️',
  admin: '👑',
  gerente: '🏢',
  supervisor: '🎯',
  encargado: '🔑',
  cajero: '💼',
  vendedor: '🛒',
  tecnico: '🔧',
  almacenista: '📦',
  bodega: '📦',
  contador: '📊',
  auditor: '🔍',
}

/** Jerarquía numérica — mayor número = más permisos en su dominio */
export const ROLE_LEVEL: Record<Role, number> = {
  auditor: 1,
  contador: 1,
  cajero: 2,
  vendedor: 2,
  tecnico: 2,
  almacenista: 2,
  bodega: 2, // legacy — igual nivel que almacenista
  encargado: 3,
  supervisor: 4,
  gerente: 4,
  admin: 5,
  director: 6, // corporativo — sobre admin
}

// ── Permisos ───────────────────────────────────────────────────────────────

export type Permission =
  // Operaciones de venta
  | 'vender'
  | 'cancelar_venta'
  | 'aplicar_descuento'
  | 'editar_precio'
  | 'venta_credito'
  | 'venta_apartado'
  // Operaciones de turno
  | 'abrir_turno'
  | 'cerrar_turno'
  // Operaciones de caja
  | 'ver_caja'
  | 'movimientos_caja'
  // Inventario
  | 'ver_inventario'
  | 'ajustar_inventario'
  | 'transferencias'
  | 'compras'
  // Reportes
  | 'ver_historial_propio'
  | 'ver_reportes_basicos'
  | 'ver_reportes_avanzados'
  | 'ver_costos'
  // Reportes corporativos (multi-tienda)
  | 'ver_reportes_corporativos'
  // Administración de tienda
  | 'gestion_clientes'
  | 'gestion_usuarios'
  | 'gestion_tiendas'
  | 'configuracion'
  | 'ver_auditoria'
  // Administración corporativa
  | 'administrar_empresas'
  | 'ver_dashboard_corporativo'
  | 'gestionar_licencias'
  // Taller
  | 'gestionar_reparaciones'
  | 'ver_acceso_dispositivo'
  | 'ver_notas_internas'
  // Garantías
  | 'gestionar_garantias'
  | 'aprobar_garantias'
  | 'devolver_efectivo_garantia'
  // Acceso multitienda (E1.3)
  | 'ver_todas_las_tiendas'
  | 'ver_tienda_asignada'
  | 'cambiar_tienda_activa'
  | 'administrar_roles'
  | 'configurar_empresa'
  | 'configurar_tienda'

export const PERMISSION_LABELS: Record<Permission, string> = {
  vender: 'Vender',
  cancelar_venta: 'Cancelar venta',
  aplicar_descuento: 'Aplicar descuento',
  editar_precio: 'Editar precio',
  venta_credito: 'Venta a crédito',
  venta_apartado: 'Venta en apartado',
  abrir_turno: 'Abrir turno',
  cerrar_turno: 'Cerrar turno',
  ver_caja: 'Ver caja',
  movimientos_caja: 'Movimientos de caja',
  ver_inventario: 'Ver inventario',
  ajustar_inventario: 'Ajustar inventario',
  transferencias: 'Transferencias',
  compras: 'Registrar compras',
  ver_historial_propio: 'Ver historial de su tienda',
  ver_reportes_basicos: 'Ver reportes básicos',
  ver_reportes_avanzados: 'Ver reportes avanzados',
  ver_costos: 'Ver costos y márgenes',
  ver_reportes_corporativos: 'Ver reportes multi-sucursal',
  gestion_clientes: 'Gestión de clientes',
  gestion_usuarios: 'Gestión de usuarios',
  gestion_tiendas: 'Gestión de tiendas',
  configuracion: 'Configuración del sistema',
  ver_auditoria: 'Ver auditoría',
  administrar_empresas: 'Administrar empresas',
  ver_dashboard_corporativo: 'Ver dashboard corporativo',
  gestionar_licencias: 'Gestionar licencias',
  gestionar_reparaciones: 'Gestionar reparaciones',
  ver_acceso_dispositivo: 'Ver acceso al dispositivo (PIN/patrón/contraseña)',
  ver_notas_internas: 'Ver notas técnicas internas (no visibles en ticket)',
  gestionar_garantias: 'Gestionar garantías',
  aprobar_garantias: 'Aprobar garantías y dar saldo a favor',
  devolver_efectivo_garantia: 'Devolver efectivo en garantía',
  ver_todas_las_tiendas: 'Ver todas las tiendas de la empresa',
  ver_tienda_asignada: 'Ver su tienda asignada',
  cambiar_tienda_activa: 'Cambiar tienda activa (multitienda)',
  administrar_roles: 'Administrar roles de usuarios',
  configurar_empresa: 'Configurar parámetros de empresa',
  configurar_tienda: 'Configurar parámetros de tienda',
}

// ── Permisos por rol ───────────────────────────────────────────────────────

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  // ── Cajero: ventas y operación básica con turno ───────────────────
  cajero: [
    'vender',
    'ver_historial_propio',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'venta_credito',
    'venta_apartado',
    'gestion_clientes',
    'ver_tienda_asignada',
  ],

  // ── Vendedor: ventas puro — sin abrir/cerrar turno ────────────────
  vendedor: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'venta_credito',
    'venta_apartado',
    'gestion_clientes',
    'ver_tienda_asignada',
  ],

  // ── Técnico: taller y reparaciones ───────────────────────────────
  tecnico: [
    'gestionar_reparaciones',
    'gestionar_garantias',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'ver_inventario',
    'ver_tienda_asignada',
  ],

  // ── Almacenista: inventario completo ─────────────────────────────
  almacenista: [
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'ver_tienda_asignada',
  ],

  // ── Bodega: legacy — conservado para compatibilidad ──────────────
  bodega: ['ver_inventario', 'ajustar_inventario', 'transferencias', 'ver_tienda_asignada'],

  // ── Encargado: operación completa de tienda ───────────────────────
  encargado: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'aprobar_garantias',
    'cancelar_venta',
    'aplicar_descuento',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'movimientos_caja',
    'venta_credito',
    'venta_apartado',
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'gestion_clientes',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_costos',
    'ver_auditoria',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'ver_tienda_asignada',
    'configurar_tienda',
  ],

  // ── Supervisor: encargado + editar precio + usuarios de tienda ────
  supervisor: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'aprobar_garantias',
    'devolver_efectivo_garantia',
    'cancelar_venta',
    'aplicar_descuento',
    'editar_precio',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'movimientos_caja',
    'venta_credito',
    'venta_apartado',
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'gestion_clientes',
    'gestion_usuarios',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_costos',
    'ver_auditoria',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'ver_tienda_asignada',
    'configurar_tienda',
  ],

  // ── Gerente: supervisor + multitienda + reportes corp ─────────────
  gerente: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'aprobar_garantias',
    'devolver_efectivo_garantia',
    'cancelar_venta',
    'aplicar_descuento',
    'editar_precio',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'movimientos_caja',
    'venta_credito',
    'venta_apartado',
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'gestion_clientes',
    'gestion_usuarios',
    'gestion_tiendas',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_reportes_corporativos',
    'ver_costos',
    'ver_auditoria',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'ver_todas_las_tiendas',
    'cambiar_tienda_activa',
    'configurar_tienda',
  ],

  // ── Admin: control total de empresa ──────────────────────────────
  admin: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'aprobar_garantias',
    'devolver_efectivo_garantia',
    'cancelar_venta',
    'aplicar_descuento',
    'editar_precio',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'movimientos_caja',
    'venta_credito',
    'venta_apartado',
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'gestion_clientes',
    'gestion_usuarios',
    'gestion_tiendas',
    'configuracion',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_reportes_corporativos',
    'ver_costos',
    'ver_auditoria',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'ver_todas_las_tiendas',
    'cambiar_tienda_activa',
    'administrar_roles',
    'configurar_empresa',
    'configurar_tienda',
  ],

  // ── Director: admin + dashboard corporativo + gestión de empresas ─
  director: [
    'vender',
    'gestionar_reparaciones',
    'gestionar_garantias',
    'aprobar_garantias',
    'devolver_efectivo_garantia',
    'cancelar_venta',
    'aplicar_descuento',
    'editar_precio',
    'abrir_turno',
    'cerrar_turno',
    'ver_caja',
    'movimientos_caja',
    'venta_credito',
    'venta_apartado',
    'ver_inventario',
    'ajustar_inventario',
    'transferencias',
    'compras',
    'gestion_clientes',
    'gestion_usuarios',
    'gestion_tiendas',
    'configuracion',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_reportes_corporativos',
    'ver_costos',
    'ver_auditoria',
    'ver_acceso_dispositivo',
    'ver_notas_internas',
    'administrar_empresas',
    'ver_dashboard_corporativo',
    'gestionar_licencias',
    'ver_todas_las_tiendas',
    'cambiar_tienda_activa',
    'administrar_roles',
    'configurar_empresa',
    'configurar_tienda',
  ],

  // ── Contador: lectura financiera ──────────────────────────────────
  contador: [
    'ver_caja',
    'compras',
    'ver_inventario',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_costos',
    'ver_auditoria',
    'ver_tienda_asignada',
  ],

  // ── Auditor: lectura total sin modificaciones ─────────────────────
  auditor: [
    'ver_caja',
    'ver_inventario',
    'ver_historial_propio',
    'ver_reportes_basicos',
    'ver_reportes_avanzados',
    'ver_costos',
    'ver_auditoria',
    'ver_tienda_asignada',
  ],
}

// ── UserPermissions ────────────────────────────────────────────────────────

export interface UserPermissions {
  userId: string
  role: Role
  storeId: string | null
  permissions: Set<Permission>
  /** Verifica si el usuario tiene un permiso específico */
  can: (permission: Permission) => boolean
  /** Verifica si el usuario tiene exactamente este rol */
  hasRole: (role: Role) => boolean
  /** Verifica si el usuario tiene al menos el nivel numérico de un rol */
  atLeast: (role: Role) => boolean
}

// ── Permisos de emergencia ─────────────────────────────────────────────────
// En modo emergencia (sistema parcialmente caído), estos permisos
// están disponibles para todos los usuarios autenticados.

export const EMERGENCY_PERMISSIONS: Permission[] = ['vender', 'abrir_turno', 'cerrar_turno']
